// ============================================================================
// FSA NOTE MODAL — LEDGERS PANE
// ============================================================================
// Lists TB ledgers currently classified into this note's classes (and the
// schedule-only "unclassified" rows mapped to this note but pending class
// split). Row actions reuse the TB-tab's classification surfaces:
//   • Edit Classification…  → FsaEditClassificationModal (single-ledger scope)
//   • Clear Classification  → fsaClassificationService.bulkClassify(null)
//
// We synthesise a TrialBalanceEntry-shaped object from the enriched
// NoteLedger so the existing modal contract stays intact — the modal owns
// the picker, the dirty tracking, and the save flow. The pane just
// refetches the note hydrate when the modal saves or the clear succeeds.
//
// TB-wise grossing up: with more than one current-year TB, "Split by TB"
// adds each TB's closing amount before the Consolidated column, with a
// totals row that ties to the consolidated total (remembered per user).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { fsaClassificationService } from 'api/services/fsa/fsaClassificationService';
import FsaEditClassificationModal from 'components/modals/modules/fsa/FsaEditClassificationModal';
import { useAuditedLedgerBalances } from 'hooks/api/fsa/useFsaAdjustmentJournals';
import { fsaTrialBalanceService } from 'api/services/fsa/fsaTrialBalanceService';
import { closingSources, ledgerKey, sourceLabel } from 'components/modules/fsa/engagement/tbSplit';
import type { TbSourceSplit } from 'api/types/fsa/trialBalance.types';
import type { NoteHydrate, NoteLedger, NoteAddedClass } from 'api/types/fsa/noteData.types';
import type { TrialBalanceEntry } from 'api/types/fsa/trialBalance.types';
import type { ID } from 'api/types';

interface LedgerRow {
  ledgerId: ID;
  ledgerName: string;
  parentGroup?: string | null;
  classCode: string;
  className: string;
  /** Sign-flipped closing for display (credit-balance schedules). */
  closingActual: number;
  /** Sign-flipped opening for display. */
  openingActual: number;
  /** RAW signed closing (storage convention). Modal/clear flows need this. */
  rawClosingActual: number;
  rawOpeningActual: number;
  /** TRUE = entry is mapped to the schedule but no specific class. */
  unclassified?: boolean;
  // Classification context — populated from the NoteLedger so the row
  // actions can call the existing classification endpoints without
  // re-fetching the underlying TB entry.
  scheduleId?: ID | null;
  scheduleClassId?: ID | null;
  /** FSA-2.2 step 4b — set when the row is mapped to a custom class. */
  customClassLocalId?: string | null;
  isAutoClassified: boolean;
  isControlAccount: boolean;
  excludeFromSignSweep: boolean;
}

interface Props {
  hydrate: NoteHydrate;
  readOnly: boolean;
  /**
   * FSA-2.2 step 4b — engagement id. Surfaced to the Edit Classification
   * modal so its picker can expose engagement-time custom classes.
   */
  engagementId?: ID | null;
  /**
   * FSA-2.2 step 4b — engagement-time custom classes declared on this Note
   * (parsed from workspace.added_classes by the parent FsaNoteModal). The
   * pane uses these to resolve a TB ledger's custom_class_local_id back to
   * the auditor's chosen label.
   */
  addedClasses?: NoteAddedClass[];
  /** Refresh the note hydrate after a successful Edit / Clear. */
  onRefetch?: () => void;
  // Minimize-to-bar plumbing for the Edit Classification modal.
  tabId?: string;
  moduleName?: string;
}

const LedgersPane: React.FC<Props> = ({
  hydrate,
  readOnly,
  engagementId,
  addedClasses,
  onRefetch,
  tabId,
  moduleName,
}) => {
  // FSA-2.2 step 4b — local_id → custom class name. Drives the row's
  // className column for ledgers mapped to a custom class.
  const customClassNameByLocalId = useMemo(() => {
    const m = new Map<string, string>();
    (addedClasses ?? []).forEach(a => m.set(a.local_id, a.name));
    return m;
  }, [addedClasses]);
  // Credit-balance schedules (liability / equity / income) display amounts
  // sign-flipped — TB stores them signed (debit +, credit −) but the auditor
  // sees natural positive numbers. Same convention as the Main Note pane.
  const sign = hydrate.isCreditBalance ? -1 : 1;

  // FSA-2.2 Adjustments — 3-column composition (Consolidated / Journal Net /
  // Audited) per ledger name. The hydrate already carries audited closing in
  // `closingActual` (= consolidated + Σ posted JE), but we need the
  // consolidated and journal-net pieces separately. Keyed by lower-trimmed
  // ledger name to match the backend aggregation key.
  const { data: auditedBalances } = useAuditedLedgerBalances(engagementId ?? null);
  const auditedByLedger = useMemo(() => {
    const m = new Map<string, { consolidated: number; journalNet: number; audited: number; isJeCreated: boolean }>();
    for (const r of auditedBalances ?? []) {
      m.set(r.ledgerName.trim().toLowerCase(), {
        consolidated: r.consolidated,
        journalNet: r.journalNet,
        audited: r.audited,
        isJeCreated: r.isJeCreated,
      });
    }
    return m;
  }, [auditedBalances]);

  const rows: LedgerRow[] = useMemo(() => {
    const out: LedgerRow[] = [];
    // Schedule-only (unclassified) ledgers FIRST — surfaces pending audit
    // work at the top so the auditor sees what needs classification before
    // reviewing already-classified ledgers below. Sentinel class label
    // ("Unclassified (pending class split)") + amber row styling carry
    // through unchanged.
    const unclassified = hydrate.unclassifiedLedgers ?? [];
    for (const l of unclassified) {
      out.push({
        ledgerId: l.ledgerId,
        ledgerName: l.ledgerName,
        parentGroup: l.parentGroup,
        classCode: '—',
        className: 'Unclassified (pending class split)',
        closingActual: l.closingActual * sign,
        openingActual: l.openingActual * sign,
        rawClosingActual: l.closingActual,
        rawOpeningActual: l.openingActual,
        unclassified: true,
        scheduleId: l.scheduleId ?? null,
        scheduleClassId: l.scheduleClassId ?? null,
        isAutoClassified: l.isAutoClassified,
        isControlAccount: l.isControlAccount,
        excludeFromSignSweep: l.excludeFromSignSweep,
      });
    }
    // Class-mapped ledgers next.
    for (const c of hydrate.liveClasses) {
      for (const l of c.ledgers) {
        out.push({
          ledgerId: l.ledgerId,
          ledgerName: l.ledgerName,
          parentGroup: l.parentGroup,
          classCode: c.classCode,
          className: c.className,
          closingActual: l.closingActual * sign,
          openingActual: l.openingActual * sign,
          rawClosingActual: l.closingActual,
          rawOpeningActual: l.openingActual,
          scheduleId: l.scheduleId ?? null,
          scheduleClassId: l.scheduleClassId ?? null,
          isAutoClassified: l.isAutoClassified,
          isControlAccount: l.isControlAccount,
          excludeFromSignSweep: l.excludeFromSignSweep,
        });
      }
    }
    // FSA-2.2 step 4b — TB ledgers mapped to engagement-time custom
    // classes. Same row shape as template-class rows; classCode = local_id
    // and className is resolved via the added_classes lookup. Tagged with
    // customClassLocalId so the Edit Classification picker pre-selects
    // the right value when the auditor re-opens the row.
    for (const cc of hydrate.liveCustomClasses ?? []) {
      // Prefer the server-resolved name (parsed from workspace.added_classes
      // on the hydrate path). Fall back to a local lookup against the
      // optional addedClasses prop, then to a generic label so the row is
      // still recognisable when the workspace JSON is unreachable.
      const className =
        cc.name
        ?? customClassNameByLocalId.get(cc.localId)
        ?? 'Custom class';
      for (const l of cc.ledgers) {
        out.push({
          ledgerId: l.ledgerId,
          ledgerName: l.ledgerName,
          parentGroup: l.parentGroup,
          classCode: cc.localId,
          className,
          closingActual: l.closingActual * sign,
          openingActual: l.openingActual * sign,
          rawClosingActual: l.closingActual,
          rawOpeningActual: l.openingActual,
          scheduleId: l.scheduleId ?? null,
          scheduleClassId: l.scheduleClassId ?? null,
          customClassLocalId: cc.localId,
          isAutoClassified: l.isAutoClassified,
          isControlAccount: l.isControlAccount,
          excludeFromSignSweep: l.excludeFromSignSweep,
        });
      }
    }
    return out;
  }, [hydrate.liveClasses, hydrate.liveCustomClasses, hydrate.unclassifiedLedgers, sign, customClassNameByLocalId]);

  // ───── TB-wise grossing up ───────────────────────────────────────────────
  const [split, setSplit] = useState<TbSourceSplit | null>(null);
  useEffect(() => {
    if (!engagementId) return;
    let live = true;
    fsaTrialBalanceService.getSourceSplit(engagementId)
      .then(s => { if (live) setSplit(s); })
      .catch(() => { if (live) setSplit(null); });
    return () => { live = false; };
  }, [engagementId, hydrate]);
  const splitSources = useMemo(() => closingSources(split), [split]);
  const canSplit = splitSources.length > 1;
  const [splitOn, setSplitOn] = useState<boolean>(() => {
    try { return localStorage.getItem(SPLIT_PREF_KEY) === '1'; } catch { return false; }
  });
  const toggleSplit = useCallback((on: boolean) => {
    setSplitOn(on);
    try { localStorage.setItem(SPLIT_PREF_KEY, on ? '1' : '0'); } catch { /* storage unavailable */ }
  }, []);
  const showSplit = canSplit && splitOn;
  /** A ledger's closing in one source, display-signed; null when that TB doesn't carry it. */
  const splitClosing = useCallback((ledgerName: string, sourceId: ID): number | null => {
    const a = split?.ledgers[ledgerKey(ledgerName)]?.find(x => x.sourceId === sourceId);
    return a?.closing != null ? a.closing * sign : null;
  }, [split, sign]);

  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      r =>
        r.ledgerName.toLowerCase().includes(q) ||
        r.className.toLowerCase().includes(q) ||
        (r.parentGroup ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totalMapped = filtered.reduce((s, r) => s + r.closingActual, 0);
  const unclassifiedCount = filtered.filter(r => r.unclassified).length;
  const unclassifiedTotal = filtered
    .filter(r => r.unclassified)
    .reduce((s, r) => s + r.closingActual, 0);

  // ───── Edit / Clear classification flows (mirror the TB tab) ──────────────
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const editModalMinimizeRef = useRef<(() => void) | null>(null);
  const [editEntry, setEditEntry] = useState<TrialBalanceEntry | null>(null);
  const [clearRow, setClearRow] = useState<LedgerRow | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  // ───── Bulk reclassify (multi-select) ─────────────────────────────────────
  // Generic affordance — same widget on every Note. The dropdown lists every
  // classification target valid for this Note (leaf template classes filtered
  // for MEMO_ONLY / MANUAL_DISCLOSURE roles + engagement-time custom classes +
  // a "schedule-only" sentinel and a "clear" sentinel). Apply fires a single
  // call to the existing bulk endpoints — no per-Note hard-coded shortcuts.
  const [selectedIds, setSelectedIds] = useState<Set<ID>>(new Set());
  const [bulkTarget, setBulkTarget] = useState<string>(''); // option key from buildBulkTargets
  const [isApplying, setIsApplying] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const SCHEDULE_ONLY_OPTION_KEY = '__SCHEDULE_ONLY__';
  const CLEAR_OPTION_KEY = '__CLEAR__';

  // Build the list of valid bulk targets — mirrors the single-row picker's
  // shape: skip MEMO_ONLY / MANUAL_DISCLOSURE (disclosure-only, no ledger
  // mapping); show custom classes with a "(custom)" suffix; trailing
  // sentinels for schedule-only and clear.
  const bulkTargets = useMemo(() => {
    type Target = {
      key: string;
      label: string;
      kind: 'leaf' | 'custom' | 'schedule_only' | 'clear';
      classId?: ID;
      customLocalId?: string;
    };
    const out: Target[] = [];
    for (const c of hydrate.liveClasses ?? []) {
      const role = typeof c.classRole === 'string' ? c.classRole.toUpperCase() : '';
      if (role === 'MEMO_ONLY' || role === 'MANUAL_DISCLOSURE') continue;
      out.push({ key: `cls::${c.classId}`, label: c.className, kind: 'leaf', classId: c.classId });
    }
    // Custom classes — merge by localId across both sources so newly-added
    // (workspace.added_classes, prop `addedClasses`) AND server-hydrated
    // (hydrate.liveCustomClasses, only populated after save+refetch) entries
    // both surface. Without the prop side, freshly-added custom classes
    // wouldn't appear in the bulk picker until the user saved and reopened
    // the Note — the per-row Classify modal already uses `addedClasses` and
    // would show them, producing the bug the user reported.
    const customKeys = new Set<string>();
    const pushCustom = (localId: string, name: string) => {
      if (customKeys.has(localId)) return;
      customKeys.add(localId);
      out.push({
        key: `cust::${localId}`,
        label: `${name} (custom)`,
        kind: 'custom',
        customLocalId: localId,
      });
    };
    for (const ac of addedClasses ?? []) {
      pushCustom(ac.local_id, ac.name ?? 'Custom class');
    }
    for (const cc of hydrate.liveCustomClasses ?? []) {
      pushCustom(cc.localId, cc.name ?? customClassNameByLocalId.get(cc.localId) ?? 'Custom class');
    }
    out.push({
      key: SCHEDULE_ONLY_OPTION_KEY,
      label: '— Schedule only (defer leaf class) —',
      kind: 'schedule_only',
    });
    out.push({
      key: CLEAR_OPTION_KEY,
      label: '— Clear classification (unmap from this Note) —',
      kind: 'clear',
    });
    return out;
  }, [hydrate.liveClasses, hydrate.liveCustomClasses, customClassNameByLocalId, addedClasses]);

  const toggleSelect = useCallback((id: ID) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback((filteredRows: LedgerRow[]) => {
    setSelectedIds(prev => {
      const allFilteredIds = filteredRows.map(r => r.ledgerId);
      const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        for (const id of allFilteredIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of allFilteredIds) next.add(id);
      return next;
    });
  }, []);

  const clearBulkSelection = useCallback(() => {
    setSelectedIds(new Set());
    setBulkTarget('');
  }, []);

  const executeBulkApply = useCallback(async () => {
    const tbId = hydrate.consolidatedTrialBalanceId;
    if (!tbId) return;
    const target = bulkTargets.find(t => t.key === bulkTarget);
    if (!target) return;
    const entryIds = Array.from(selectedIds);
    if (entryIds.length === 0) return;
    setIsApplying(true);
    try {
      if (target.kind === 'leaf' && target.classId) {
        await fsaClassificationService.bulkClassify(tbId, {
          entryIds,
          scheduleClassId: target.classId,
        });
      } else if (target.kind === 'custom' && target.customLocalId && hydrate.scheduleId) {
        await fsaClassificationService.bulkClassify(tbId, {
          entryIds,
          customClassLocalId: target.customLocalId,
          scheduleId: hydrate.scheduleId,
        });
      } else if (target.kind === 'schedule_only' && hydrate.scheduleId) {
        await fsaClassificationService.bulkScheduleMap(tbId, {
          entryIds,
          scheduleId: hydrate.scheduleId,
        });
      } else if (target.kind === 'clear') {
        await fsaClassificationService.bulkClassify(tbId, {
          entryIds,
          scheduleClassId: null,
          customClassLocalId: null,
        });
      }
      notificationService.toast.success(
        `Reclassified ${entryIds.length} ledger${entryIds.length === 1 ? '' : 's'} as ${target.label}.`,
      );
      clearBulkSelection();
      onRefetch?.();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      notificationService.toast.error(msg || 'Bulk reclassification failed.');
    } finally {
      setIsApplying(false);
      setBulkConfirmOpen(false);
    }
  }, [
    hydrate.consolidatedTrialBalanceId,
    hydrate.scheduleId,
    bulkTargets,
    bulkTarget,
    selectedIds,
    onRefetch,
    clearBulkSelection,
  ]);

  const handleBulkApplyClick = useCallback(() => {
    if (selectedIds.size === 0 || !bulkTarget) return;
    // Per the design rule: confirm when the batch is large (> 20). Below
    // that, apply immediately — power-users doing many small batches don't
    // want a click-confirm overhead.
    if (selectedIds.size > 20) {
      setBulkConfirmOpen(true);
      return;
    }
    void executeBulkApply();
  }, [selectedIds, bulkTarget, executeBulkApply]);

  // Translate the enriched NoteLedger row into the TrialBalanceEntry shape
  // the existing Edit Classification modal expects. Fields the modal does
  // not read (rowVersion / createdAt / displayOrder etc.) get conservative
  // defaults so the type contract holds.
  const synthEntry = useCallback((row: LedgerRow): TrialBalanceEntry => ({
    id: row.ledgerId,
    trialBalanceId: hydrate.consolidatedTrialBalanceId ?? ('' as ID),
    ledgerName: row.ledgerName,
    parentGroup: row.parentGroup ?? null,
    openingAmount: row.rawOpeningActual,
    closingAmount: row.rawClosingActual,
    scheduleId: row.scheduleId ?? null,
    scheduleClassId: row.scheduleClassId ?? null,
    scheduleClassCode: null,
    scheduleClassName: row.unclassified ? null : row.className,
    // FSA-2.2 step 4b — carries the custom-class id back into the Edit
    // Classification modal so its picker pre-selects the right value.
    customClassLocalId: row.customClassLocalId ?? null,
    scheduleCode: hydrate.scheduleCode,
    scheduleName: hydrate.scheduleName,
    isAutoClassified: row.isAutoClassified,
    classifiedAt: null,
    classifiedBy: null,
    classifiedByName: null,
    isControlAccount: row.isControlAccount,
    excludeFromSignSweep: row.excludeFromSignSweep,
    displayOrder: 0,
    rowVersion: 0,
    createdAt: '',
    updatedAt: null,
  }), [hydrate.consolidatedTrialBalanceId, hydrate.scheduleCode, hydrate.scheduleName]);

  const handleEditClick = useCallback((row: LedgerRow) => {
    if (!hydrate.consolidatedTrialBalanceId) {
      notificationService.toast.warning('No consolidated TB on this engagement yet.');
      return;
    }
    const synth = synthEntry(row);
    if (tabId) {
      const modalId = `${tabId}-fsa-edit-cls-${synth.id}`;
      if (isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
    }
    // Auto-minimize any other row's modal that's already open so the user
    // can compare without losing their picker state.
    if (editEntry && editEntry.id !== synth.id && editModalMinimizeRef.current) {
      editModalMinimizeRef.current();
    }
    setEditEntry(synth);
  }, [
    hydrate.consolidatedTrialBalanceId,
    synthEntry,
    tabId,
    isMinimized,
    getMinimizedModal,
    dismissMinimized,
    editEntry,
  ]);

  const handleRestoreEditModal = useCallback((entry: TrialBalanceEntry) => {
    setEditEntry(entry);
  }, []);

  const handleClearClassification = useCallback(async () => {
    if (!clearRow || !hydrate.consolidatedTrialBalanceId) return;
    setIsClearing(true);
    try {
      await fsaClassificationService.bulkClassify(hydrate.consolidatedTrialBalanceId, {
        entryIds: [clearRow.ledgerId],
        scheduleClassId: null,
      });
      notificationService.toast.success(
        `Classification cleared on '${clearRow.ledgerName}'.`,
      );
      setClearRow(null);
      onRefetch?.();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || 'Failed to clear classification.',
      );
    } finally {
      setIsClearing(false);
    }
  }, [clearRow, hydrate.consolidatedTrialBalanceId, onRefetch]);

  const buildLedgerRowActions = useCallback((row: LedgerRow): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'edit',
        label: row.unclassified ? 'Classify…' : 'Reclassify…',
        icon: <IconSystem.edit size={16} />,
        onClick: () => handleEditClick(row),
      },
    ];
    if (row.scheduleId || row.scheduleClassId) {
      items.push({
        id: 'clear',
        label: 'Clear Classification',
        icon: <IconSystem.trash size={16} />,
        onClick: () => setClearRow(row),
        variant: 'danger',
      });
    }
    return items;
  }, [handleEditClick]);

  // Header select-all state — checked when every filtered row is selected,
  // indeterminate when some-but-not-all are.
  const headerAllSelected =
    filtered.length > 0 && filtered.every(r => selectedIds.has(r.ledgerId));
  const headerSomeSelected =
    !headerAllSelected && filtered.some(r => selectedIds.has(r.ledgerId));
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = headerSomeSelected;
    }
  }, [headerSomeSelected]);

  return (
    <div style={paneStyle}>
      <div style={toolbarStyle}>
        <div style={searchWrapStyle}>
          <IconSystem.search size={14} />
          <input
            type="text"
            placeholder="Search ledger / group / class…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={{ flex: 1 }} />
        {canSplit && (
          <label style={splitSwitchStyle} title="Show each uploaded current-year TB's closing amount before the consolidated column">
            <input type="checkbox" checked={splitOn} onChange={e => toggleSplit(e.target.checked)} />
            Split by TB
          </label>
        )}
        <span style={countPillStyle}>
          {filtered.length} of {rows.length} ledger(s)
        </span>
      </div>
      {!readOnly && selectedIds.size > 0 && (
        <div style={bulkToolbarStyle}>
          <span style={bulkCountPillStyle}>
            {selectedIds.size} selected
          </span>
          <select
            value={bulkTarget}
            onChange={e => setBulkTarget(e.target.value)}
            style={bulkSelectStyle}
            disabled={isApplying}
            aria-label="Bulk reclassification target"
          >
            <option value="">Change Classification…</option>
            {bulkTargets.map(t => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleBulkApplyClick}
            disabled={isApplying || !bulkTarget}
            title={
              bulkTarget
                ? `Apply '${bulkTargets.find(t => t.key === bulkTarget)?.label ?? ''}' to ${selectedIds.size} ledger${selectedIds.size === 1 ? '' : 's'}`
                : 'Pick a classification target first'
            }
          >
            Apply ({selectedIds.size})
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={clearBulkSelection}
            disabled={isApplying}
          >
            Clear selection
          </button>
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <table style={tableStyle}>
          <thead style={theadStyle}>
            <tr>
              {!readOnly && (
                <th style={{ ...thStyle, width: 32, textAlign: 'center' }}>
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={headerAllSelected}
                    onChange={() => toggleSelectAllFiltered(filtered)}
                    aria-label="Select all visible ledgers"
                    title="Select all ledgers currently visible (respects the search filter)"
                  />
                </th>
              )}
              <th style={thStyle}>Ledger</th>
              <th style={thStyle}>Group</th>
              <th style={thStyle}>Class</th>
              {showSplit && splitSources.map(s => (
                <th key={String(s.id)} style={{ ...thStyle, ...splitColStyle, textAlign: 'right' }} title={`Closing in ${s.originalFileName || sourceLabel(s)}`}>
                  {sourceLabel(s)}
                </th>
              ))}
              <th style={{ ...thStyle, textAlign: 'right' }} title="Raw closing from the consolidated TB (pre-adjustment).">
                Consolidated
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }} title="Σ POSTED adjustment-journal lines for this ledger.">
                Journal Net
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }} title="Consolidated + Journal Net — drives BS / P&L / CF / Notes.">
                Audited
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Opening</th>
              {!readOnly && (
                <th style={{ ...thStyle, width: 60, textAlign: 'center' }} />
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={(readOnly ? 7 : 9) + (showSplit ? splitSources.length : 0)} style={emptyRowStyle}>
                  No ledgers mapped to this note.
                </td>
              </tr>
            ) : (
              filtered.map(r => {
                // Cross-reference the audited-balances map by ledger name.
                // Fall back to (closingActual, 0, closingActual) when no JE
                // composition row exists for the ledger (engagement has no
                // posted JEs yet) — audited = consolidated.
                const a = auditedByLedger.get(r.ledgerName.trim().toLowerCase());
                const consolidated = a ? a.consolidated * sign : (r.rawClosingActual - 0) * sign;
                const journalNet   = a ? a.journalNet   * sign : 0;
                const audited      = a ? a.audited      * sign : r.closingActual;
                const isJeCreated  = a?.isJeCreated ?? false;
                return (
                  <tr
                    key={r.ledgerId}
                    style={r.unclassified ? unclassifiedRowStyle : undefined}
                  >
                    {!readOnly && (
                      <td style={{ ...tdStyle, textAlign: 'center', width: 32 }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.ledgerId)}
                          onChange={() => toggleSelect(r.ledgerId)}
                          aria-label={`Select ${r.ledgerName}`}
                        />
                      </td>
                    )}
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>
                        {r.ledgerName}
                        {isJeCreated && (
                          <span
                            title="JE-introduced ledger — created by a posted journal entry, not present in the source TB."
                            style={jeBadgeStyle}
                          >
                            JE
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                        {r.parentGroup ?? '—'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {r.unclassified ? (
                        <span style={unclassifiedChipStyle} title="Mapped to this schedule, no specific class assigned yet — Reclassify to pick a leaf class.">
                          ⚠ Unclassified
                        </span>
                      ) : (
                        <>
                          <div style={{ fontWeight: 500 }}>{r.className}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                            {r.classCode}
                          </div>
                        </>
                      )}
                    </td>
                    {showSplit && splitSources.map(s => {
                      const v = splitClosing(r.ledgerName, s.id);
                      return (
                        <td key={String(s.id)} style={{ ...amountCellStyle, ...splitColStyle }}>
                          {v == null ? <span style={{ color: 'var(--text-secondary)' }}>—</span> : fmtAmt(v)}
                        </td>
                      );
                    })}
                    <td style={amountCellStyle}>{fmtAmt(consolidated)}</td>
                    <td
                      style={{
                        ...amountCellStyle,
                        color: Math.abs(journalNet) < 0.005 ? 'var(--text-secondary)' : '#92400e',
                        fontWeight: Math.abs(journalNet) < 0.005 ? 400 : 600,
                      }}
                    >
                      {Math.abs(journalNet) < 0.005 ? '—' : fmtAmt(journalNet)}
                    </td>
                    <td style={{ ...amountCellStyle, fontWeight: 600 }}>{fmtAmt(audited)}</td>
                    <td
                      style={{
                        ...amountCellStyle,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {fmtAmt(r.openingActual)}
                    </td>
                    {!readOnly && (
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <ActionMenu
                          items={buildLedgerRowActions(r)}
                          position="bottom-right"
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
          {showSplit && filtered.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...tdStyle, ...splitTotalStyle }} colSpan={readOnly ? 3 : 4}>Total</td>
                {splitSources.map(s => (
                  <td key={String(s.id)} style={{ ...amountCellStyle, ...splitColStyle, ...splitTotalStyle }}>
                    {fmtAmt(filtered.reduce((sum, r) => sum + (splitClosing(r.ledgerName, s.id) ?? 0), 0))}
                  </td>
                ))}
                <td style={{ ...amountCellStyle, ...splitTotalStyle }}>
                  {fmtAmt(filtered.reduce((sum, r) => {
                    const a = auditedByLedger.get(r.ledgerName.trim().toLowerCase());
                    return sum + (a ? a.consolidated * sign : r.rawClosingActual * sign);
                  }, 0))}
                </td>
                <td style={{ ...tdStyle, ...splitTotalStyle }} colSpan={readOnly ? 3 : 4} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div style={footerStyle}>
        <div>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
            Total mapped:
          </span>{' '}
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            ₹ {fmtAmt(totalMapped)}
          </span>
          {unclassifiedCount > 0 && (
            <>
              {' · '}
              <span style={{ color: '#92400e', fontSize: '0.75rem', fontWeight: 600 }}>
                ⚠ {unclassifiedCount} unclassified ({fmtAmt(unclassifiedTotal)})
              </span>
            </>
          )}
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
          Use Reclassify to move a ledger; Clear unbinds it from this note entirely.
        </div>
      </div>

      {/* Edit Classification modal — single-row scope. Same modal the
          Trial Balance tab uses, hydrated with a synthesised entry.
          Note-scoped: pass scopeScheduleId so the picker defaults to
          THIS Note's classes instead of the full template tree (the
          auditor's already inside this Note's Ledgers pane). */}
      {hydrate.consolidatedTrialBalanceId && (
        <FsaEditClassificationModal
          isOpen={editEntry != null}
          trialBalanceId={hydrate.consolidatedTrialBalanceId}
          entry={editEntry}
          templateId={hydrate.templateId ?? null}
          engagementId={engagementId ?? null}
          scopeScheduleId={hydrate.scheduleId ?? null}
          onClose={() => setEditEntry(null)}
          onSaved={() => {
            setEditEntry(null);
            onRefetch?.();
          }}
          tabId={tabId}
          moduleName={moduleName}
          onRestoreModal={handleRestoreEditModal}
          minimizeRef={editModalMinimizeRef}
        />
      )}

      {/* Bulk apply confirm — only fires when N > 20 per the agreed
          large-batch safeguard. Below that, the Apply button writes
          immediately (power-user friendly for many small batches). */}
      <ConfirmDialog
        open={bulkConfirmOpen}
        title="Reclassify in Bulk"
        message={(() => {
          const t = bulkTargets.find(x => x.key === bulkTarget);
          const labelTxt = t ? t.label : '';
          return `About to reclassify ${selectedIds.size} ledgers to '${labelTxt}'. This is a large batch — confirm to proceed.`;
        })()}
        confirmLabel="Apply"
        cancelLabel="Cancel"
        variant="default"
        loading={isApplying}
        loadingMessage="Applying…"
        onConfirm={() => { void executeBulkApply(); }}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      {/* Clear Classification confirm — matches the TB-tab copy / variant. */}
      <ConfirmDialog
        open={clearRow != null}
        title="Clear Classification"
        message={clearRow ? `Reset '${clearRow.ledgerName}' back to unmapped? It will leave this note entirely. Any leaf or schedule-only mapping on this ledger will be removed.` : ''}
        confirmLabel="Clear"
        cancelLabel="Cancel"
        variant="danger"
        loading={isClearing}
        loadingMessage="Clearing…"
        onConfirm={handleClearClassification}
        onCancel={() => setClearRow(null)}
      />
    </div>
  );
};

const SPLIT_PREF_KEY = 'fsa.notes.ledgers.splitByTb';
const splitColStyle: React.CSSProperties = { background: 'var(--bg-secondary)' };
const splitTotalStyle: React.CSSProperties = { fontWeight: 700, borderTop: '2px solid var(--border-color)' };
const splitSwitchStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 600,
  cursor: 'pointer', userSelect: 'none', marginRight: 10, color: 'var(--text-primary)',
};

const fmtAmt = (v: number) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Distinct visual for unclassified rows so they stand out as audit work.
const unclassifiedRowStyle: React.CSSProperties = {
  background: '#fffbeb',
};
const unclassifiedChipStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  cursor: 'help',
};

// ───── styles ────────────────────────────────────────────────────────────────

const paneStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};
const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const searchWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  border: '1px solid var(--form-border-color)',
  borderRadius: 6,
  padding: '0 8px',
  background: 'var(--bg-primary)',
  width: 260,
};
const searchInputStyle: React.CSSProperties = {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  flex: 1,
  fontSize: '0.8rem',
  padding: '4px 0',
  color: 'var(--text-primary)',
};
const countPillStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};
const bulkToolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  background: 'var(--bg-secondary, #f1f5f9)',
  borderBottom: '1px solid var(--border-color)',
};
const bulkCountPillStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  color: '#1e40af',
  background: '#dbeafe',
  padding: '2px 8px',
  borderRadius: 9,
  whiteSpace: 'nowrap',
};
const bulkSelectStyle: React.CSSProperties = {
  fontSize: '0.78rem',
  padding: '4px 8px',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  background: 'var(--bg-primary, #fff)',
  color: 'var(--text-primary)',
  minWidth: 260,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const theadStyle: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 1 };
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-color)',
};
// FSA-2.2 Adjustments — shared style for the right-aligned numeric columns
// (Consolidated / Journal Net / Audited / Opening).
const amountCellStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
// Small amber "JE" badge marking journal-introduced ledgers (no source TB row).
const jeBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.6rem',
  fontWeight: 700,
  padding: '1px 4px',
  borderRadius: 3,
  lineHeight: 1,
  verticalAlign: 'middle',
};
const emptyRowStyle: React.CSSProperties = {
  padding: '24px',
  textAlign: 'center',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

const footerStyle: React.CSSProperties = {
  borderTop: '1px solid var(--border-color)',
  padding: '0.5rem 0.75rem',
  background: 'var(--bg-secondary)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

export default LedgersPane;
